// 数式評価ユーティリティ
// PouchComponentから移動した関数群

export interface ExpressionResult {
  hasError: boolean;
  result?: {
    value: number;
    multiplier: number;
    isTime: boolean;
  };
  lastExpression?: string;
}

export interface EvaluateResult {
  value: number;
  isTimeValue: boolean;
  finalOperator?: string;
  finalMultiplier?: number;
}

export interface ApplyFinalResult {
  hasError: boolean;
  result?: {
    value: number;
    multiplier: number;
    isTime: boolean;
  };
}

// 値の正規化関数
const normalizeValue = (value: number): number => {
  const isNegative = value < 0;
  const absValue = Math.abs(value);
  const roundedValue = Math.round(absValue);
  return isNegative ? -roundedValue : roundedValue;
};

// 単一演算子ケースの処理
export const handleSingleOperatorCase = (expression: string, currentValue: number, currentMultiplier: number): { value: number, isTimeValue: boolean, finalOperator?: string, finalMultiplier?: number } => {
  if (/^\*{2,}$/.test(expression)) {
    // マルチプライヤーを値に適用してリセット
    const newValue = currentMultiplier >= 0 
      ? currentValue * currentMultiplier 
      : currentValue / Math.abs(currentMultiplier);
    // 時間モードを切った時も、元々時間計算だった値は整数で表示
    const roundedValue = Math.round(newValue);
    return { value: roundedValue, isTimeValue: false };
  } else if (/^\/{2,}$/.test(expression)) {
    // マルチプライヤーを値に適用してリセット（割り算版）
    const newValue = currentMultiplier >= 0 
      ? currentValue / currentMultiplier 
      : currentValue * Math.abs(currentMultiplier);
    return { value: newValue, isTimeValue: false };
  } else if (/^\:{2,}$/.test(expression)) {
    // 時間形式変換（actual値を使用）
    const actualValue = currentValue * currentMultiplier;
    const hours = Math.floor(Math.abs(actualValue) / 60);
    const minutes = Math.round(Math.abs(actualValue) % 60); // 分を四捨五入
    const sign = actualValue < 0 ? -1 : 1;
    const timeValue = sign * (hours * 60 + minutes);
    // 時間形式では60*マルチの形式で返す（整数のみ）
    const baseValue = timeValue >= 0 ? 60 : -60;
    const newMultiplier = Math.abs(timeValue) / 60; // 整数の分なので小数は出ない
    return { value: baseValue, isTimeValue: true, finalMultiplier: newMultiplier };
  } else if (/^\~{2,}$/.test(expression)) {
    // 丸め処理（10の位で丸め）
    const rounded = Math.round(currentValue / 10) * 10;
    return { value: rounded, isTimeValue: false };
  }
  
  return { value: currentValue, isTimeValue: false };
};

// 統一数式評価関数
export const evaluateUnifiedExpression = (expression: string, startsWithNewValue: boolean, currentValue: number, currentMultiplier: number, skipFinalOperator: boolean = false, currentIsTime: boolean = false): EvaluateResult => {
  // 0. カンマを除去し、x,X,×,÷を*,/に正規化
  let normalizedExpression = expression.replace(/,/g, '').replace(/[xX×]/g, '*').replace(/÷/g, '/');
  
  // 末尾演算子の検出と除去（skipFinalOperatorがtrueの場合はスキップ）
  let finalOperator: string | undefined;
  let finalMultiplier: number | undefined;
  if (!skipFinalOperator) {
    // 単純な数値/数値パターンを先にチェック
    const simpleDivMatch = normalizedExpression.match(/^(\d+(?:\.\d+)?)\/(\d+(?:\.\d+)?)$/);
    if (simpleDivMatch) {
      const baseValue = parseFloat(simpleDivMatch[1]);
      const divisor = parseFloat(simpleDivMatch[2]);
      normalizedExpression = simpleDivMatch[1];
      finalOperator = '/';
      finalMultiplier = divisor;
      
    } else {
      // 末尾から乗除が続く場合の処理（末尾の乗除部分のみ）
      const trailingMulDivMatch = normalizedExpression.match(/(.*?)([\*\/](?:-?\d+(?:\.\d+)?[\*\/])*-?\d+(?:\.\d+)?)$/);
      if (trailingMulDivMatch) {
        const baseExpression = trailingMulDivMatch[1];
        const trailingExpression = trailingMulDivMatch[2];
              
        // 末尾の乗除式を計算
        try {
          // 先頭が演算子の場合は1を補完
          let expressionToEvaluate = trailingExpression;
          if (trailingExpression.match(/^[\+\-\*\/]/)) {
            expressionToEvaluate = '1' + trailingExpression;
          }
          const trailingResult = evaluateExpression(expressionToEvaluate);
          if (!isNaN(trailingResult) && isFinite(trailingResult)) {
            normalizedExpression = baseExpression;
            finalOperator = '*'; // 乗除が混ざる場合は*にする
            finalMultiplier = trailingResult;
            
            
            // マルチプライヤーの範囲チェック：9999999999以下でなければエラー
            if (Math.abs(finalMultiplier) > 9999999999) {
              console.error('evaluateUnifiedExpression: マルチプライヤーが範囲外です（9999999999を超えています）:', finalMultiplier);
              finalMultiplier = undefined;
            }
          }
        } catch (error) {
          console.error('evaluateUnifiedExpression: 末尾乗除計算エラー:', error);
        }
      } else {
        // 従来の単一演算子の処理
        const lastOperatorMatch = normalizedExpression.match(/(.*)([\*\/])(-?\d+(?:\.\d+)?)$/);
        if (lastOperatorMatch) {
          normalizedExpression = lastOperatorMatch[1];
          finalOperator = lastOperatorMatch[2];
          finalMultiplier = parseFloat(lastOperatorMatch[3]);
          
          // マルチプライヤーの範囲チェック：9999999999以下でなければエラー
          if (Math.abs(finalMultiplier) > 9999999999) {
            console.error('evaluateUnifiedExpression: マルチプライヤーが範囲外です（9999999999を超えています）:', finalMultiplier);
            // エラーの場合はundefinedに設定してエラー状態を示す
            finalMultiplier = undefined;
          }
        }
      }
    }
  } else {

  }
  
  // 時間形式を分に変換し、時間トークンの位置を記録
  const timeTokens = new Set<number>(); // 時間トークンのインデックス
  let tokenIndex = 0;
  
  const timeConverted = normalizedExpression.replace(/(-?)(\d*):(\d*)/g, (match, minus, hours, minutes) => {
    timeTokens.add(tokenIndex);
    tokenIndex++;
    
    let h = hours === '' ? 0 : parseInt(hours);
    let m = minutes === '' ? 0 : parseInt(minutes);
    if (isNaN(h) || isNaN(m) || m < 0 || h < 0) return match;
    
    // 分が60以上の場合は時間に変換
    if (m >= 60) {
      const extraHours = Math.floor(m / 60);
      h += extraHours;
      m = m % 60;
    }
    
    const totalMinutes = (h * 60 + m) * (minus === '-' ? -1 : 1);
    return totalMinutes.toString();
  });
  
  // トークン化
  const tokens: string[] = [];
  let currentToken = '';
  tokenIndex = 0;
  
  for (let i = 0; i < timeConverted.length; i++) {
    const char = timeConverted[i];
    if (char === '+' || char === '-' || char === '*' || char === '/' || char === '^' || char === '%' || char === ':' || char === '~') {
      if (currentToken) {
        tokens.push(currentToken);
        tokenIndex++;
        currentToken = '';
      }
      if (char === '-' && i === 0 && startsWithNewValue) {
        // 新しい値から始まる場合のみ負号として扱う
        currentToken = char;
      } else if (char === '-' && tokens.length > 0 && 
                 ['*', '/', '^', '%', '~'].includes(tokens[tokens.length - 1])) {
        // 演算子の直後の負号は数値の一部として扱う
        currentToken = char;
      } else if (char === '+' && i === 0 && startsWithNewValue) {
        // 新しい値から始まる場合の+は継続計算として扱う（++と同じ）
        tokens.push(char);
      } else if (char === '+' && tokens.length > 0 && 
                 ['*', '/', '^', '%', '~'].includes(tokens[tokens.length - 1])) {
        // 演算子の直後の+は数値の一部として扱う
        currentToken = char;
      } else {
        tokens.push(char);
      }
    } else if (char === '?') {
      // ?は未知数として扱う（方程式ソルバー用）
      if (currentToken) {
        tokens.push(currentToken);
        tokenIndex++;
        currentToken = '';
      }
      tokens.push('?');
      tokenIndex++;
    } else if (char === '=') {
      // =は方程式の区切りとして扱う（方程式ソルバー用）
      if (currentToken) {
        tokens.push(currentToken);
        tokenIndex++;
        currentToken = '';
      }
      tokens.push('=');
      tokenIndex++;
    } else {
      currentToken += char;
    }
  }
  if (currentToken) {
    tokens.push(currentToken);
  }
  
  // 初期値設定
  let result: number;
  let isTimeValue = false;
  
  if (startsWithNewValue) {
    result = parseFloat(tokens[0]);
    isTimeValue = timeTokens.has(0); // 最初のトークンが時間かどうか
  } else {
    // 通常の場合：基本値のみ使用
    // 時間かどうかは現在のパウチのisTime状態を使用
    result = currentValue;
    isTimeValue = currentIsTime;
    
    // 時間パウチの場合：マルチプライヤーを適用した実際の値を開始値として使用
    if (currentIsTime) {
      result = currentMultiplier >= 0 
        ? currentValue * currentMultiplier 
        : currentValue / Math.abs(currentMultiplier);
    }
    
    // %%の場合：マルチプライヤーを適用した値から開始
    if (tokens.length > 0 && tokens[0] === '%') {
      result = currentMultiplier >= 0 
        ? currentValue * currentMultiplier 
        : currentValue / Math.abs(currentMultiplier);
    }
  }
  // 継続計算パターンの場合、トークンの最初に現在の値を追加
  let processedTokens = [...tokens];
  let timeTokensOffset = 0;
  if (!startsWithNewValue && isNaN(parseFloat(tokens[0]))) {
    // 継続計算パターン：現在の値を最初に追加
    let valueToAdd = currentValue;
    if (currentIsTime) {
      // 時間パウチの場合：マルチプライヤーを適用した実際の値を整数に丸めて使用
      const actualValue = currentMultiplier >= 0 
        ? currentValue * currentMultiplier 
        : currentValue / Math.abs(currentMultiplier);
      valueToAdd = Math.round(actualValue); // 整数に丸める
      timeTokens.add(0); // 最初のトークンを時間トークンとしてマーク
    }
    processedTokens.unshift(valueToAdd.toString());
    timeTokensOffset = 1; // 時間トークンのインデックスを1つずらす
  }
  

  
  // 左から右に順次処理
  let i = 0;
  
  while (i < processedTokens.length - 1) {
    const operator = processedTokens[i+1];
    const nextValue = parseFloat(processedTokens[i+2]);
    const nextIsTime = timeTokens.has(i+2-timeTokensOffset);
    const previousResult = result;

    if (operator === '+') {
      result += nextValue;
    } else if (operator === '-') {
      result -= nextValue;
    } else if (operator === '*') {
      result *= nextValue;
    } else if (operator === '/' && nextValue !== 0) {
      result /= nextValue;
      if (isTimeValue && nextIsTime) {
        isTimeValue = false; // 時間÷時間は実数
      }
    } else if (operator === '%' && nextValue !== 0) {
      result = result % nextValue;
      if (isTimeValue && nextIsTime) {
        isTimeValue = false; // 時間%時間は実数
      }
    } else if (operator === '^') {
      result = Math.pow(result, nextValue);
    } else if (operator === '~' && nextValue !== 0) {
      // 丸め処理
      result = Math.round(result / nextValue) * nextValue;
    }
    
    // 時間性の更新（÷と%以外）
    if (operator !== '/' && operator !== '%') {
      isTimeValue = isTimeValue || nextIsTime;
    }


    i += 2; // 次の演算子と値のペアに移動
  }
  
  // 時間計算の場合は最終結果も整数に丸める
  const finalResult = isTimeValue ? Math.round(result) : result;
  return { value: finalResult, isTimeValue, finalOperator, finalMultiplier };
};

// 最終マルチプライヤー適用
export const applyFinalMultiplier = (result: { value: number, isTimeValue: boolean }, operator: string, multiplier: number): ApplyFinalResult => {

  
  // マルチが0の場合はエラー
  if (multiplier === 0) {
    console.error('マルチが0になることはできません');
    return { hasError: true };
  }
  
  // マルチプライヤーの範囲チェック：9999999999以下でなければエラー
  if (Math.abs(multiplier) > 9999999999) {
    console.error('マルチプライヤーが範囲外です（9999999999を超えています）:', multiplier);
    return { hasError: true };
  }
  
  if (result.isTimeValue) {
    // 時間の場合：最終マルチプライヤーを時間計算に適用
    let finalTimeValue = result.value;
    if (operator === '*') {
      finalTimeValue *= multiplier;
    } else if (operator === '/') {
      finalTimeValue /= multiplier;
    }
    
    // エラーチェック：計算結果が無効な場合はエラーとして扱う
    if (isNaN(finalTimeValue) || !isFinite(finalTimeValue)) {
      console.error('時間計算結果が無効です（NaN/無限大）:', finalTimeValue);
      return { hasError: true };
    }
    
    // 最終値の範囲チェック：9999999999以下でなければエラー
    if (Math.abs(finalTimeValue) > 9999999999) {
      console.error('時間計算結果が範囲外です（9999999999を超えています）:', finalTimeValue);
      return { hasError: true };
    }
    
    const baseValue = finalTimeValue >= 0 ? 60 : -60;
    const timeMultiplier = Math.abs(finalTimeValue) / 60; // 整数の分なので小数は出ない
    // NaNチェック
    if (!isNaN(baseValue) && !isNaN(timeMultiplier) && isFinite(baseValue) && isFinite(timeMultiplier)) {
      return { hasError: false, result: { value: baseValue, multiplier: timeMultiplier, isTime: true } };
    }
  } else {
    // 実数の場合：マルチプライヤー適用
    if (multiplier < 0) {
      // 負数：値正負転換
      const newValue = -result.value;
      
      // エラーチェック：計算結果が無効な場合はエラーとして扱う
      if (isNaN(newValue) || !isFinite(newValue)) {
        console.error('負数計算結果が無効です（NaN/無限大）:', newValue);
        return { hasError: true };
      }
      
      // 最終値の範囲チェック：9999999999以下でなければエラー
      if (Math.abs(newValue) > 9999999999) {
        console.error('負数計算結果が範囲外です（9999999999を超えています）:', newValue);
        return { hasError: true };
      }
      
      const absMultiplier = Math.abs(multiplier);
      if (operator === '*') {
        // *負数：マルチは絶対値
        if (!isNaN(newValue) && !isNaN(absMultiplier) && isFinite(newValue) && isFinite(absMultiplier)) {
          return { hasError: false, result: { value: newValue, multiplier: absMultiplier, isTime: false } };
        }
      } else {
        // /負数：マルチは絶対値*-1
        if (!isNaN(newValue) && !isNaN(absMultiplier) && isFinite(newValue) && isFinite(absMultiplier)) {
          return { hasError: false, result: { value: newValue, multiplier: -absMultiplier, isTime: false } };
        }
      }
    } else {
      // 正数：通常処理
      if (operator === '*') {
        // 掛け算の場合：値はそのまま、マルチプライヤーを設定
        const normalizedValue = normalizeValue(result.value);
        
        // エラーチェック：計算結果が無効な場合はエラーとして扱う
        if (isNaN(normalizedValue) || !isFinite(normalizedValue)) {
          console.error('掛け算計算結果が無効です（NaN/無限大）:', normalizedValue);
          return { hasError: true };
        }
        
        // 最終値の範囲チェック：9999999999以下でなければエラー
        if (Math.abs(normalizedValue) > 9999999999) {
          console.error('掛け算計算結果が範囲外です（9999999999を超えています）:', normalizedValue);
          return { hasError: true };
        }
        
        if (!isNaN(normalizedValue) && !isNaN(multiplier) && isFinite(normalizedValue) && isFinite(multiplier)) {
          return { hasError: false, result: { value: normalizedValue, multiplier, isTime: false } };
        }
      } else {
        // 割り算の場合：値はそのまま、マルチプライヤーを負数で設定
        const normalizedValue = normalizeValue(result.value);
        
        
        // エラーチェック：計算結果が無効な場合はエラーとして扱う
        if (isNaN(normalizedValue) || !isFinite(normalizedValue)) {
          console.error('割り算計算結果が無効です（NaN/無限大）:', normalizedValue);
          return { hasError: true };
        }
        
        // 最終値の範囲チェック：9999999999以下でなければエラー
        if (Math.abs(normalizedValue) > 9999999999) {
          console.error('割り算計算結果が範囲外です（9999999999を超えています）:', normalizedValue);
          return { hasError: true };
        }
        
        const finalMultiplier = -multiplier;
        if (!isNaN(normalizedValue) && !isNaN(finalMultiplier) && isFinite(normalizedValue) && isFinite(finalMultiplier)) {
          return { hasError: false, result: { value: normalizedValue, multiplier: finalMultiplier, isTime: false } };
        }
      }
    }
  }
  
  // 正常に完了した場合はfalse（エラーなし）を返す
  return { hasError: false };
};

// 式をトークン化するヘルパー関数
const tokenizeExpression = (expression: string): string[] => {
  const tokens: string[] = [];
  let currentToken = '';
  
  for (let i = 0; i < expression.length; i++) {
    const char = expression[i];
    if (char === '+' || char === '-' || char === '*' || char === '/' || char === '^' || char === '%' || char === '~') {
      if (currentToken) {
        tokens.push(currentToken);
        currentToken = '';
      }
      tokens.push(char);
    } else if (char === 'x') {
      if (currentToken) {
        tokens.push(currentToken);
        currentToken = '';
      }
      tokens.push('x');
    } else if (char === '?') {
      // ?は未知数として扱う（方程式ソルバー用）
      if (currentToken) {
        tokens.push(currentToken);
        currentToken = '';
      }
      tokens.push('?');
    } else if (char === '=') {
      // =は方程式の区切りとして扱う（方程式ソルバー用）
      if (currentToken) {
        tokens.push(currentToken);
        currentToken = '';
      }
      tokens.push('=');
    } else {
      currentToken += char;
    }
  }
  
  if (currentToken) {
    tokens.push(currentToken);
  }
  
  return tokens;
};

// 式を評価する関数
const evaluateExpression = (expression: string): number => {
  try {
    // カンマを除去してからトークン化
    const cleanedExpression = expression.replace(/,/g, '');
    const tokens = tokenizeExpression(cleanedExpression);
    let result = parseFloat(tokens[0]);
    
    for (let i = 1; i < tokens.length; i += 2) {
      if (i + 1 < tokens.length) {
        const operator = tokens[i];
        const nextValue = parseFloat(tokens[i + 1]);
        
        switch (operator) {
          case '+': result += nextValue; break;
          case '-': result -= nextValue; break;
          case '*': result *= nextValue; break;
          case '/': result /= nextValue; break;
          case '^': result = Math.pow(result, nextValue); break;
          case '%': result = result % nextValue; break;
          case '~': result = Math.round(result / nextValue) * nextValue; break;
        }
      }
    }
    
    return result;
  } catch (error) {
    console.error('evaluateExpression error:', error);
    throw error;
  }
};

// 左辺を右からXまで逆算してXの値を求める関数
const solveForX = (tokens: string[], rightValue: number): number => {
  try {
    // 1. 左から順次計算：左側の式を先に計算
    let leftSideValue = 0;
    let i = 0;
    
    // 最初の数値を取得
    if (tokens[i] && !isNaN(parseFloat(tokens[i]))) {
      leftSideValue = parseFloat(tokens[i]);
      i++;
    }
    
    // 左から順次計算
    while (i < tokens.length - 1) {
      const operator = tokens[i];
      const nextToken = tokens[i + 1];
      
      if (nextToken === '?') {
        // ?が見つかった場合、計算を停止
        break;
      } else if (!isNaN(parseFloat(nextToken))) {
        // 数値の場合、計算を実行
        const nextValue = parseFloat(nextToken);
        switch (operator) {
          case '+': leftSideValue += nextValue; break;
          case '-': leftSideValue -= nextValue; break;
          case '*': leftSideValue *= nextValue; break;
          case '/': leftSideValue /= nextValue; break;
          case '^': leftSideValue = Math.pow(leftSideValue, nextValue); break;
          case '%': leftSideValue = leftSideValue % nextValue; break;
          case '~': leftSideValue = Math.round(leftSideValue / nextValue) * nextValue; break;
        }
        i += 2;
      } else {
        i++;
      }
    }
    
    // 2. 右から逆算：計算結果を使って逆算
    let remainingValue = rightValue;
    
    for (let j = tokens.length - 1; j >= 0; j--) {
      const token = tokens[j];
      
      if (token === '?') {
        // ?が見つかった場合、左側の式の計算結果を使って逆算
        if (j > 0) {
          const operator = tokens[j - 1];
          
          // 左側の式の計算結果を使って逆算
          let finalX: number;
          switch (operator) {
            case '+': finalX = remainingValue - leftSideValue; break;
            case '-': finalX = leftSideValue - remainingValue; break;
            case '*': finalX = remainingValue / leftSideValue; break;
            case '/': finalX = leftSideValue / remainingValue; break;
            case '^': finalX = Math.pow(remainingValue, 1 / leftSideValue); break;
            case '%': finalX = remainingValue; break; // 簡略化
            case '~': finalX = remainingValue; break; // 簡略化
            default: finalX = remainingValue; break;
          }
          
          return finalX;
        } else {
          // ?の左側に演算子がない場合
          return remainingValue;
        }
      } else if (!isNaN(parseFloat(token))) {
        // 数値の場合、次の演算子で逆算
        if (j > 0) {
          const operator = tokens[j - 1];
          const value = parseFloat(token);
          
          // 逆算の実行
          switch (operator) {
            case '+': remainingValue -= value; break;
            case '-': remainingValue += value; break;
            case '*': remainingValue /= value; break;
            case '/': remainingValue *= value; break;
            case '^': remainingValue = Math.pow(remainingValue, 1 / value); break;
            case '%': remainingValue += value; break; // 簡略化
            case '~': remainingValue += value; break; // 簡略化
          }
          
          j--; // 演算子も処理したので、さらに1つ戻る
        }
      }
    }
    
    console.error('solveForX: ?が見つかりませんでした');
    return NaN;
    
  } catch (error) {
    console.error('solveForXV2 error:', error);
    return NaN;
  }
};

// 統一された方程式ソルバー関数
const solveEquation = (expression: string, startsWithNewValue: boolean, currentValue: number, currentMultiplier: number) => {
  try {
    // カンマを除去
    expression = expression.replace(/,/g, '');
    
    // 1. エラーチェック：?やイコールが非連続複数、いずれかがない場合をエラーにする
    if (!expression.includes('?') || !expression.includes('=')) {
      console.error('solveEquation: ?または=が含まれていません');
      return null;
    }
    
    // 2. 方程式分割
    const [leftSide, rightSide] = expression.split('=');
    if (!leftSide || !rightSide) {
      console.error('solveEquation: 方程式の分割に失敗しました');
      return null;
    }
    
    // 3. 右辺に演算があれば演算する
    let rightValue: number;
    try {
      rightValue = evaluateExpression(rightSide);
    } catch (error) {
      console.error('solveEquation右辺演算エラー:', error);
      return null;
    }
    
    // 4. 左辺の構築
    let leftTokens = tokenizeExpression(leftSide);
    
    // startsWithNewValue=falseの場合、現在の値を左辺の最初に追加
    if (!startsWithNewValue) {
      leftTokens.unshift(currentValue.toString());
    }
    
    // 左辺の最後が掛け算か割り算なら、現在のマルチを無視する
    // それ以外は現在のマルチを左辺トークンの右端に追加する
    const lastOperator = leftTokens.length >= 2 ? leftTokens[leftTokens.length - 2] : '';
    const shouldIgnoreMultiplier = ['*', '/'].includes(lastOperator);
    
    if (!shouldIgnoreMultiplier && currentMultiplier !== 1) {
      leftTokens.push('*', currentMultiplier.toString());
    }
    
    // 5. 左辺を右からXまで逆算する
    let x = solveForX(leftTokens, rightValue);
    if (isNaN(x) || !isFinite(x)) {
      console.error('solveEquation: Xの解が無効です:', x);
      return null;
    }
    
    // 6. X解が決定したあとの左辺が決まる
    let solvedExpression = leftTokens.join('').replace(/\?/g, x.toString());
    
    // 7. 右辺を捨てて、normalizedとする
    
    return { solvedExpression };
    
  } catch (error) {
    console.error('solveEquation error:', error);
    return null;
  }
};

// 統一数式処理関数（PouchComponent用）
export const handleUnifiedExpression = (expression: string, currentValue: number, currentMultiplier: number, currentIsTime: boolean = false): ExpressionResult => {
  // lastExpressionの処理用変数を最初に宣言
  let finalLastExpression: string | undefined;

  
  // 0. カンマを除去し、x,X,×,÷を*,/に正規化
  expression = expression.replace(/,/g, '').replace(/[xX×]/g, '*').replace(/÷/g, '/');
  
  // 時間形式の3項目以降を無視（1:20:30 → 1:20）
  expression = expression.replace(/(\d+:\d+)(:\d+)+/g, '$1');
  
  // 小数以下が6桁以上の数値チェック（エラーとする）
  const decimalPattern = /\d+\.\d{6,}/g;
  if (decimalPattern.test(expression)) {
    return { hasError: true, lastExpression: undefined };
  }
  
  // 1. 初期値判定：ダブル以上の連続演算子判定を正規化前に行う
  const startsWithDouble = /^(\+{2,}|\-{2,}|\*{2,}|\/{2,}|\^{2,}|\%{2,}|\:{2,}|\~{2,})/.test(expression);
  let startsWithNewValue = !startsWithDouble && /^(\d|[\-]?\d*:\d*|[\-]\d|\?)/.test(expression);
  // 2. 演算子単体なら特殊ケース処理をして終わる（シングルとダブル以上を区別）
  if (/^(\*{2,}|\/{2,}|\:{2,}|\~{2,})$/.test(expression)) {
    const result = handleSingleOperatorCase(expression, currentValue, currentMultiplier);
    // :の時間形式変換ではfinalMultiplierを使用、その他は1にリセット
    const finalMultiplier = /^\:{2,}$/.test(expression) && result.finalMultiplier !== undefined ? result.finalMultiplier : 1;
    return { hasError: false, result: { value: result.value, multiplier: finalMultiplier, isTime: result.isTimeValue }, lastExpression: undefined };
  }
  
  // 2.5. 単一の演算子を*1として解釈（エラーとしない）
  if (/^(\*|\/|\/1|\*1)$/.test(expression)) {
    // *1として処理（値はそのまま、マルチは1）
    return { hasError: false, result: { value: currentValue, multiplier: 1, isTime: false }, lastExpression: undefined };
  }

  // 2.55. 単独の - / + で、実際の値の符号に応じて正負反転（正のとき - のみ、負のとき + のみ）
  const signFlipOnly = expression.trim();
  if ((signFlipOnly === '-' || signFlipOnly === '+') && !startsWithDouble) {
    const actual =
      currentMultiplier >= 0
        ? currentValue * currentMultiplier
        : currentValue / Math.abs(currentMultiplier);
    if (currentIsTime) {
      const rounded = Math.round(actual);
      if (signFlipOnly === '-' && rounded > 0) {
        const newMinutes = -rounded;
        const baseVal = newMinutes >= 0 ? 60 : -60;
        const mult = Math.abs(newMinutes) / 60;
        return {
          hasError: false,
          result: { value: baseVal, multiplier: mult, isTime: true },
          lastExpression: undefined,
        };
      }
      if (signFlipOnly === '+' && rounded < 0) {
        const newMinutes = -rounded;
        const baseVal = newMinutes >= 0 ? 60 : -60;
        const mult = Math.abs(newMinutes) / 60;
        return {
          hasError: false,
          result: { value: baseVal, multiplier: mult, isTime: true },
          lastExpression: undefined,
        };
      }
    } else {
      if (signFlipOnly === '-' && actual > 0) {
        return {
          hasError: false,
          result: {
            value: normalizeValue(-currentValue),
            multiplier: currentMultiplier,
            isTime: false,
          },
          lastExpression: undefined,
        };
      }
      if (signFlipOnly === '+' && actual < 0) {
        return {
          hasError: false,
          result: {
            value: normalizeValue(-currentValue),
            multiplier: currentMultiplier,
            isTime: false,
          },
          lastExpression: undefined,
        };
      }
    }
  }
  
  // 2.6. 単一の.は明示的にエラー
  if (/^\.$/.test(expression)) {
    console.error('単一の.は無効な入力です');
    return { hasError: true, lastExpression: undefined };
  }
  
  // 2.7. 単一の:も特殊ケース処理（時間形式変換）
  if (/^:$/.test(expression)) {
    const result = handleSingleOperatorCase('::', currentValue, currentMultiplier);
    const finalMultiplier = result.finalMultiplier !== undefined ? result.finalMultiplier : currentMultiplier;
    return { hasError: false, result: { value: result.value, multiplier: finalMultiplier, isTime: result.isTimeValue }, lastExpression: undefined };
  }
  
  // 3. 連続する同じ記号をシングルに正規化する（ミスタイプ対応、2個以上のみ）
  let normalized = expression
    .replace(/\+{2,}/g, '+')         // +が2個以上連続 → +
    .replace(/\-{2,}/g, '-')         // -が2個以上連続 → -
    .replace(/\*{2,}/g, '*')         // *が2個以上連続 → *
    .replace(/\/{2,}/g, '/')         // /が2個以上連続 → /
    .replace(/\^{2,}/g, '^')         // ^が2個以上連続 → ^
    .replace(/\%{2,}/g, '%')         // %が2個以上連続 → %
    .replace(/\~{2,}/g, '~')         // ~が2個以上連続 → ~
    .replace(/\?{2,}/g, '?')         // ?が2個以上連続 → ?
    .replace(/\={2,}/g, '=')         // =が2個以上連続 → =
    .replace(/\:{2,}(\d+)/g, '++:$1')  // :が2個以上連続+数字 → ++:数字
    .replace(/\:{2,}/g, '++:');        // :が2個以上連続 → ++:
  
  // 3.5. 単独または末尾の演算子を*1として解釈
  normalized = normalized
    .replace(/^\/1$/, '*1')           // 単独の/1 → *1
    .replace(/^\/$/, '*1')            // 単独の/ → *1
    .replace(/^(\*|\/)$/, '*1')       // 単独の* → *1
    .replace(/(\*|\/)$/, '*1')        // 末尾の*または/ → *1
    .replace(/(\*|\/)1$/, '*1');      // 末尾の*1または/1 → *1
  
  // 3.6. ドットで始まる数値に0を補完、ドットの後に数値がない場合は0を補完して整数に
  let normalized2 = normalized;
  normalized = normalized
    .replace(/^\.(\d+)/, '0.$1')      // .5 → 0.5
    .replace(/^\.$/, '0')             // . → 0
    .replace(/([+\-*/^%:~]|^)\.(\d+)/g, '$10.$2')  // +.5 → +0.5, *.5 → *0.5
    .replace(/([+\-*/^%:~]|^)\.$/g, '$10')          // +. → +0, *. → *0
    .replace(/(\d+)\.(?!\d)/g, '$1');                // 5. → 5, 10. → 10 (ドットの後に数値がない場合のみ)
  
  // 3.7. ドット処理後のstartsWithNewValueを再設定
  if (normalized !== expression && normalized !== normalized2) {
    // ドット処理で正規化が行われた場合、startsWithNewValueを再判定
    const normalizedStartsWithDouble = /^(\+{2,}|\-{2,}|\*{2,}|\/{2,}|\^{2,}|\%{2,}|\:{2,}|\~{2,})/.test(normalized);
    startsWithNewValue = !normalizedStartsWithDouble && /^(\d|[\-]?\d*:\d*|[\-]\d|\?)/.test(normalized);
  }
  
  // 3.8. 演算子n%変換直前のnormalized値を記録対象として保存
  const lastExpression = normalized;
  
  // 演算をしていない単純入力の場合はlastExpressionを空にする
  // 時間形式を分に変換してから演算子チェック
  const timeConvertedForCheck = expression.replace(/(-?)(\d*):(\d*)/g, (match, minus, hours, minutes) => {
    let h = hours === '' ? 0 : parseInt(hours);
    let m = minutes === '' ? 0 : parseInt(minutes);
    if (isNaN(h) || isNaN(m) || m < 0 || h < 0) return match;
    
    // 分が60以上の場合は時間に変換
    if (m >= 60) {
      const extraHours = Math.floor(m / 60);
      h += extraHours;
      m = m % 60;
    }
    
    const totalMinutes = (h * 60 + m) * (minus === '-' ? -1 : 1);
    return totalMinutes.toString();
  });
  
  // 単純な数字入力の場合はlastExpressionを空にする
  if (!/[+\-*/×÷xX^%:~＋－＊／＝％：～?=]/.test(timeConvertedForCheck)) {
    finalLastExpression = "";
  } else if (!startsWithNewValue) {
    if (currentIsTime) {
      // 時間パウチの場合：時間形式で保存
      const totalMinutes = Math.round(Math.abs(currentValue * currentMultiplier)); // 整数に丸める
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      const sign = currentValue * currentMultiplier < 0 ? '-' : '';
      const timeString = `${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
      finalLastExpression = timeString + lastExpression;
    } else {
      finalLastExpression = currentValue + lastExpression;
    }
  } else {
    finalLastExpression = lastExpression;
  }
  
  // 4. 演算子n%を変換する
  const beforePercentageConversion = normalized;
  normalized = normalized
    // +n%を*(1+n/100)に変換、-n%を*(1-n/100)に変換
    .replace(/([\+\-])(\d+(?:\.\d+)?)%(?![0-9])/g, (match, operator, number) => {
      const multiplier = operator === '+' ? (1 + parseFloat(number) / 100) : (1 - parseFloat(number) / 100);
      return '*' + multiplier;
    })
    // *n%を*(n/100)に変換、/n%を/(n/100)に変換
    .replace(/(\*|\/|:)(\d+(?:\.\d+)?)%(?![0-9])/g, (match, operator, number) => {
      const percentage = parseFloat(number) / 100;
      return operator + percentage;
    });
  
  // 4.5. +n%変換が行われた場合はstartsWithNewValueを再判定
  if (normalized !== beforePercentageConversion && /^[\+\-]\d+(?:\.\d+)?%/.test(beforePercentageConversion)) {
    // 変換後の文字列が演算子で始まる場合はfalse、数字で始まる場合はtrue
    startsWithNewValue = /^(\d|[\-]?\d*:\d*|[\-]\d|\?)/.test(normalized);
  }
  
  // 5. 演算子n~を変換する（丸め処理）
  normalized = normalized
    // 丸め演算子を特殊な関数呼び出し形式に変換
    .replace(/(\d+(?:\.\d+)?)~(\d+(?:\.\d+)?)/g, (match, value, roundTo) => {
      const val = parseFloat(value);
      const round = parseFloat(roundTo);
      const rounded = Math.round(val / round) * round;
      return rounded.toString();
    });
  
  // 5.5. 方程式ソルバー処理
  if (normalized.includes('?') && normalized.includes('=')) {
    // =が複数回出現する場合はエラー
    const equalCount = (normalized.match(/=/g) || []).length;
    if (equalCount > 1) {
      console.error('Multiple equals signs are not allowed');
      return { hasError: true, lastExpression: undefined };
    }
    
    const equationResult = solveEquation(normalized, startsWithNewValue, currentValue, currentMultiplier);
    
    if (equationResult) {
      // 方程式が解けた場合、解を従来の計算処理に渡す
      normalized = equationResult.solvedExpression;
    } else {
      // 方程式が解けない場合はエラー処理
      console.error('Equation cannot be solved');
      return { hasError: true, lastExpression: undefined };
    }
  }
  
  // 6. 残ったものを左から順に計算する
  const result = evaluateUnifiedExpression(normalized, startsWithNewValue, currentValue, currentMultiplier, false, currentIsTime);
  
  // 7. 結果とマルチ設定（NaN対策）
  if (result.finalMultiplier !== undefined && result.finalOperator !== undefined) {
    // evaluateUnifiedExpression内で末尾演算子が検出された場合
    const finalResult = applyFinalMultiplier(result, result.finalOperator, result.finalMultiplier);
    if (finalResult.hasError) {
      console.error('applyFinalMultiplierでエラーが発生しました');
      return { hasError: true, lastExpression: undefined };
    }
    return { hasError: false, result: finalResult.result, lastExpression: finalLastExpression };
  } else if (result.finalOperator !== undefined && result.finalMultiplier === undefined) {
    // finalMultiplierがundefinedの場合はエラー（範囲外のマルチプライヤー）
    console.error('マルチプライヤーが範囲外のためエラーです');
    return { hasError: true, lastExpression: undefined };
  } else {
    // エラーチェック：結果が無効な場合はエラーとして扱う（値の更新前にチェック）
    if (isNaN(result.value) || !isFinite(result.value)) {
      console.error('計算結果が無効です（NaN/無限大）:', result.value);
      return { hasError: true, lastExpression: undefined };
    }
    
    // 最終値の範囲チェック：9999999999以下でなければエラー（値の更新前にチェック）
    if (Math.abs(result.value) > 9999999999) {
      console.error('計算結果が範囲外です（9999999999を超えています）:', result.value);
      return { hasError: true, lastExpression: undefined };
    }
    
    // 末尾演算子がない場合
    if (result.isTimeValue) {
      const baseVal = result.value >= 0 ? 60 : -60;
      const multiplier = Math.abs(result.value) / 60; // 整数の分なので小数は出ない

      // NaNチェック
      if (!isNaN(baseVal) && !isNaN(multiplier) && isFinite(baseVal) && isFinite(multiplier)) {
        return { 
          hasError: false, 
          result: { value: baseVal, multiplier, isTime: true }, 
          lastExpression: finalLastExpression 
        };
      }
    } else {
      const finalValue = normalizeValue(result.value);

      // NaNチェック
      if (!isNaN(finalValue) && isFinite(finalValue)) {
        // %が含まれている場合はマルチプライヤーを1にリセット
        const finalMultiplier = normalized.includes('%') ? 1 : currentMultiplier;
        
        return { 
          hasError: false, 
          result: { value: finalValue, multiplier: finalMultiplier, isTime: false }, 
          lastExpression: finalLastExpression 
        };
      }
    }
  }
  
  // 正常に完了した場合は成功を示すオブジェクトを返す（lastExpressionも含める）
  return { hasError: false, lastExpression: finalLastExpression };
};

